import api from '@/store/api';
import { athena, boro } from '@/store/api/instances';
import type { BulkCreateCaseRequest, BulkCreateCaseResponse, Case } from '@/types';
import type { ApiListingRequest, ApiResponse } from '@bitstopco/bitstop-theme';
import { type SkipToken, skipToken } from '@reduxjs/toolkit/query';

type BlobPart = Blob | ArrayBuffer;

export const casesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getCases: build.query<ApiResponse<Case[]>, ApiListingRequest>({
      query: (data) => {
        return athena({
          url: 'v2/operations/cases',
          method: 'POST',
          data,
        });
      },
      providesTags: ['Case'],
    }),
    getCase: build.query<ApiResponse<Case>, string>({
      query: (id) => athena({ url: `v2/Operations/cases/${id}` }),
      transformResponse: (response: ApiResponse<Case>) => {
        if (response?.data?.notes) {
          response.data.notes = [...response.data.notes].sort((a, b) => {
            const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
            const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
            return dateB - dateA; // Newest first
          });
        }
        return response;
      },
      providesTags: (_, __, id) => [{ type: 'Case', id }],
    }),
    getCasesStats: build.query<ApiResponse<any>, void>({
      query: () =>
        athena({
          url: 'v2/Operations/cases/stats',
          method: 'GET',
        }),
    }),
    createCase: build.mutation<
      ApiResponse<Case>,
      {
        atm_id: number;
        issue_ids?: string[];
        type?: string;
        priority?: string;
        assigned_user_id?: string;
        merchant_id?: string;
        description?: string;
      }
    >({
      query: (data) =>
        boro({
          url: 'v2/Operations/cases',
          method: 'POST',
          data,
        }),
      invalidatesTags: ['Case'],
    }),
    bulkCreateCases: build.mutation<ApiResponse<BulkCreateCaseResponse>, BulkCreateCaseRequest>({
      query: (data) =>
        boro({
          url: 'v2/Operations/cases/bulk',
          method: 'POST',
          data,
        }),
      invalidatesTags: ['Case'],
    }),
    upsertCase: build.mutation<ApiResponse<Case>, Partial<Case> & { id: string }>({
      query: ({ id, ...data }) =>
        boro({
          url: `v2/Operations/cases/${id}`,
          method: 'PATCH',
          data,
        }),
      invalidatesTags: (_, __, { id }) => [{ type: 'Case', id }, 'Case', 'Issue'],
    }),
    exportCases: build.mutation<BlobPart, { kind: string; data: ApiListingRequest }>({
      query: ({ kind, data }) => {
        return athena({
          url: `v2/Operations/cases/${kind}`,
          method: 'POST',
          data,
          responseType: 'blob',
        });
      },
    }),
  }),
});

/**
 * RTK Query API for Case Resources.
 */
export const caseResourcesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getCaseResources: build.query<
      ApiResponse<{
        statuses: string[];
        priorities: string[];
      }>,
      void
    >({
      query: () =>
        athena({
          url: 'v2/Resources/cases',
          method: 'GET',
        }),
    }),
  }),
});

export const useGetCases = (data: ApiListingRequest | SkipToken) => {
  return casesApi.useGetCasesQuery(data, {
    skip: data === skipToken,
  });
};

export const useGetCase = (id: string) => {
  return casesApi.useGetCaseQuery(id);
};

export const {
  useUpsertCaseMutation,
  useCreateCaseMutation,
  useBulkCreateCasesMutation,
  useExportCasesMutation,
} = casesApi;

export const { useGetCaseResourcesQuery } = caseResourcesApi;

export const useGetCasesStats = () => {
  return casesApi.useGetCasesStatsQuery();
};
